import { useEffect, useRef } from 'react';
import { useEditorStore } from '../stores/editor-store';
import { useProjectsStore } from '../stores/projects-store';
import { getPageSnapshots } from '../utils/grapes-page-snapshots';

const AUTO_SAVE_DELAY = 2000; // 2 seconds

export function useAutoSave() {
  const editor = useEditorStore((s) => s.editorInstance);
  const currentProject = useProjectsStore((s) => s.currentProject);
  const saveCurrentContent = useProjectsStore((s) => s.saveCurrentContent);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!editor || !currentProject) return;

    const handleChange = () => {
      // Skip auto-save while content is being bulk-loaded
      if (useEditorStore.getState().isLoadingContent) return;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        // Double-check loading state when the timeout fires
        if (useEditorStore.getState().isLoadingContent) return;

        const html = editor.getHtml() || '';
        const css = editor.getCss() || '';
        const components = editor.getComponents().toJSON();
        const styles = editor.getStyle();
        const projectData = (editor as any).getProjectData ? (editor as any).getProjectData() : undefined;
        const pageSnapshots = getPageSnapshots(editor as any);
        saveCurrentContent({ html, css, components, styles, projectData, pageSnapshots });
      }, AUTO_SAVE_DELAY);
    };

    editor.on('component:add', handleChange);
    editor.on('component:remove', handleChange);
    editor.on('component:update', handleChange);
    editor.on('style:change', handleChange);

    return () => {
      editor.off('component:add', handleChange);
      editor.off('component:remove', handleChange);
      editor.off('component:update', handleChange);
      editor.off('style:change', handleChange);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [editor, currentProject, saveCurrentContent]);
}
